import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, User, Clock, CheckCircle, Eye, MessageSquare, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { campaignAPI } from '../../../api';
import { useFormat } from '../../../i18n/format.js';

function CampaignResponsesModal({ campaign, onClose }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const { data: responsesData, isLoading } = useQuery({
    queryKey: ['campaign-responses', campaign.id],
    queryFn: () => campaignAPI.getResponses(campaign.id),
    select: (data) => data?.data || []
  });

  const responses = responsesData || [];

  // Same response-status set as CampaignDetailModal — reuses campaigns.responseStatus.*.
  const getStatusConfig = (status) => {
    const configs = {
      sent: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      read: { color: 'bg-blue-100 text-blue-800', icon: Eye },
      responded: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      expired: { color: 'bg-red-100 text-red-800', icon: Clock },
      dismissed: { color: 'bg-gray-200 text-gray-600', icon: X }
    };
    const key = configs[status] ? status : 'sent';
    return { ...configs[key], label: t(`campaigns.responseStatus.${key}`) };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('campaigns.responses.title', { name: campaign.name })}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('campaigns.responses.driverCount', { count: responses.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{t('campaigns.responses.empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.driver')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.organization')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.sentOn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.readOn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('campaigns.responses.table.respondedOn')}
                    </th>
                    {campaign.requires_response && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('campaigns.responses.table.response')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {responses.map((response) => {
                    const statusConfig = getStatusConfig(response.status);
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr key={response.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {response.first_name} {response.last_name}
                              </div>
                              <div className="text-sm text-gray-500">{response.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {response.org_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {response.sent_at
                            ? fmt.date(response.sent_at)
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {response.read_at
                            ? fmt.date(response.read_at)
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {response.responded_at
                            ? fmt.date(response.responded_at)
                            : '-'}
                        </td>
                        {campaign.requires_response && (
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {response.response_value || response.response_text || '-'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('campaigns.responses.receivedCount', { count: responses.filter(r => r.status === 'responded').length })}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignResponsesModal;
